import { useState, useMemo } from 'react'
import { ArrowUpDown, ArrowUp, ArrowDown, ChevronDown, ChevronRight } from 'lucide-react'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { formatNumber, formatCurrency, formatPercent } from '@/lib/utils'
import type {
  UIBlock,
  TableBlock,
  TableColumn,
  MarkdownBlock,
  MetricsBlock,
  MetricItem,
  NoteBlock,
  ActionsBlock,
  RawJsonBlock,
  GraphBlock,
} from '@/types/blocks'
import { InlineApproval } from '@/components/chat/blocks/InlineApproval'
import { InlineInputRequest } from '@/components/chat/blocks/InlineInputRequest'

// ============================================================================
// Shared Markdown Components (reused from MarkdownBlock.tsx)
// ============================================================================

const markdownComponents = {
  h1: ({ children }: { children?: React.ReactNode }) => (
    <h1 className="text-2xl font-bold mt-6 mb-4 first:mt-0">{children}</h1>
  ),
  h2: ({ children }: { children?: React.ReactNode }) => (
    <h2 className="text-xl font-semibold mt-5 mb-3">{children}</h2>
  ),
  h3: ({ children }: { children?: React.ReactNode }) => (
    <h3 className="text-lg font-medium mt-4 mb-2">{children}</h3>
  ),
  p: ({ children }: { children?: React.ReactNode }) => <p className="my-2 leading-relaxed">{children}</p>,
  ul: ({ children }: { children?: React.ReactNode }) => <ul className="my-2 ml-4 list-disc">{children}</ul>,
  ol: ({ children }: { children?: React.ReactNode }) => <ol className="my-2 ml-4 list-decimal">{children}</ol>,
  li: ({ children }: { children?: React.ReactNode }) => <li className="my-1">{children}</li>,
  a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
      {children}
    </a>
  ),
  code: ({ children, className }: { children?: React.ReactNode; className?: string }) => {
    const isInline = !className
    if (isInline) {
      return <code className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono">{children}</code>
    }
    return <code className="block bg-muted p-4 rounded-lg text-sm font-mono overflow-x-auto">{children}</code>
  },
  pre: ({ children }: { children?: React.ReactNode }) => (
    <pre className="bg-muted p-4 rounded-lg overflow-x-auto my-4">{children}</pre>
  ),
  blockquote: ({ children }: { children?: React.ReactNode }) => (
    <blockquote className="border-l-4 border-primary bg-primary/5 pl-4 pr-3 py-2 rounded-r-md my-4 text-foreground font-medium">
      {children}
    </blockquote>
  ),
  hr: () => <hr className="my-4 border-border" />,
  table: ({ children }: { children?: React.ReactNode }) => (
    <div className="overflow-x-auto my-4 rounded-lg border border-border shadow-sm">
      <table className="min-w-full border-collapse text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }: { children?: React.ReactNode }) => (
    <thead className="bg-primary text-primary-foreground">{children}</thead>
  ),
  tbody: ({ children }: { children?: React.ReactNode }) => (
    <tbody className="divide-y divide-border">{children}</tbody>
  ),
  tr: ({ children }: { children?: React.ReactNode }) => (
    <tr className="even:bg-muted/40 hover:bg-muted/70 transition-colors">{children}</tr>
  ),
  th: ({ children }: { children?: React.ReactNode }) => (
    <th className="px-4 py-3 text-left font-semibold tracking-wide whitespace-nowrap">{children}</th>
  ),
  td: ({ children }: { children?: React.ReactNode }) => (
    <td className="px-4 py-2.5 text-foreground">{children}</td>
  ),
  strong: ({ children }: { children?: React.ReactNode }) => <strong className="font-bold">{children}</strong>,
  em: ({ children }: { children?: React.ReactNode }) => <em className="italic">{children}</em>,
}

// ============================================================================
// ChatBlockRenderer — Inline (Card-free) block rendering
// ============================================================================

interface ChatBlockRendererProps {
  block: UIBlock
}

export function ChatBlockRenderer({ block }: ChatBlockRendererProps) {
  switch (block.type) {
    case 'markdown':
      return <InlineMarkdown block={block} />
    case 'table':
      return <InlineTable block={block} />
    case 'metrics':
      return <InlineMetrics block={block} />
    case 'note':
      return <InlineNote block={block} />
    case 'actions':
      return <InlineActions block={block} />
    case 'raw_json':
      return <InlineRawJson block={block} />
    case 'graph':
      return <InlineGraph block={block} />
    case 'approval':
      return <InlineApproval block={block} />
    case 'input_request':
      return <InlineInputRequest block={block} />
    default: {
      const _exhaustiveCheck: never = block
      return <InlineRawJson block={{ type: 'raw_json', data: { json: _exhaustiveCheck as Record<string, unknown>, collapsed: true } }} />
    }
  }
}

// ============================================================================
// Inline Markdown
// ============================================================================

function InlineMarkdown({ block }: { block: MarkdownBlock }) {
  return (
    <div>
      {block.title && <h4 className="font-semibold text-sm mb-2">{block.title}</h4>}
      <div className="prose prose-sm dark:prose-invert max-w-none">
        <Markdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
          {block.data.content}
        </Markdown>
      </div>
    </div>
  )
}

// ============================================================================
// Inline Table (with sorting, no Card wrapper)
// ============================================================================

function formatCellValue(value: unknown, column: TableColumn): React.ReactNode {
  if (value === null || value === undefined) {
    return <span className="text-muted-foreground">—</span>
  }
  const stringValue = String(value)
  switch (column.type) {
    case 'badge':
      return <Badge variant={getBadgeVariant(stringValue)}>{stringValue}</Badge>
    case 'date':
      try { return new Date(stringValue).toLocaleDateString() } catch { return stringValue }
    case 'number':
      return (
        <span className="font-mono tabular-nums">
          {typeof value === 'number' ? value.toLocaleString() : stringValue}
        </span>
      )
    case 'link':
      return (
        <a href={stringValue} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
          {stringValue}
        </a>
      )
    default:
      return stringValue
  }
}

function getBadgeVariant(status: string): 'default' | 'success' | 'warning' | 'destructive' | 'secondary' {
  const normalized = status.toLowerCase()
  if (['approved', 'active', 'success', 'completed', 'done'].includes(normalized)) return 'success'
  if (['pending', 'waiting', 'in_progress', 'processing'].includes(normalized)) return 'warning'
  if (['rejected', 'failed', 'error', 'cancelled'].includes(normalized)) return 'destructive'
  return 'secondary'
}

type SortDirection = 'asc' | 'desc' | null

function InlineTable({ block }: { block: TableBlock }) {
  const { title, data } = block
  const { columns, rows, pagination } = data
  const [sortColumn, setSortColumn] = useState<string | null>(null)
  const [sortDirection, setSortDirection] = useState<SortDirection>(null)

  const handleSort = (columnKey: string) => {
    if (sortColumn === columnKey) {
      if (sortDirection === 'asc') setSortDirection('desc')
      else if (sortDirection === 'desc') { setSortColumn(null); setSortDirection(null) }
      else setSortDirection('asc')
    } else {
      setSortColumn(columnKey)
      setSortDirection('asc')
    }
  }

  const sortedRows = useMemo(() => {
    if (!sortColumn || !sortDirection) return rows
    return [...rows].sort((a, b) => {
      const aValue = a[sortColumn]
      const bValue = b[sortColumn]
      if (aValue === bValue) return 0
      if (aValue === null || aValue === undefined) return 1
      if (bValue === null || bValue === undefined) return -1
      const comparison = String(aValue).localeCompare(String(bValue), undefined, { numeric: true, sensitivity: 'base' })
      return sortDirection === 'asc' ? comparison : -comparison
    })
  }, [rows, sortColumn, sortDirection])

  return (
    <div className="mt-8 mb-4 animate-fade-in-up">
      {title && <h2 className="font-medium text-lg mb-4 truncate" title={title}>{title}</h2>}
      <div className="rounded-lg border shadow-sm overflow-x-auto">
        <Table>
          <TableHeader className="bg-muted/50">
            <TableRow className="hover:bg-muted/50">
              {columns.map((column) => (
                <TableHead key={column.key} className="h-12 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  {column.sortable !== false ? (
                    <Button variant="ghost" size="sm" className="-ml-3 h-10 text-xs font-bold uppercase tracking-wider" onClick={() => handleSort(column.key)}>
                      {column.label}
                      {sortColumn === column.key ? (
                        sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4" /> : <ArrowDown className="ml-2 h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="ml-2 h-4 w-4 opacity-50" />
                      )}
                    </Button>
                  ) : (
                    column.label
                  )}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  <span className="text-muted-foreground">No data available</span>
                </TableCell>
              </TableRow>
            ) : (
              sortedRows.map((row, rowIndex) => (
                <TableRow key={rowIndex}>
                  {columns.map((column) => (
                    <TableCell
                      key={column.key}
                      className="animate-row-slide-in"
                      style={{ animationDelay: `${rowIndex * 60}ms` }}
                    >
                      {formatCellValue(row[column.key], column)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      {pagination && (
        <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
          <span>Showing {Math.min(pagination.pageSize, rows.length)} of {pagination.total} results</span>
          <span>Page {pagination.page} of {Math.ceil(pagination.total / pagination.pageSize)}</span>
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Inline Metrics
// ============================================================================

function formatMetricValue(value: string | number, format?: string): string {
  if (typeof value === 'string') return value
  switch (format) {
    case 'currency_inr': return formatCurrency(value, 'INR')
    case 'percent': return formatPercent(value)
    default: return formatNumber(value)
  }
}

function InlineMetrics({ block }: { block: MetricsBlock }) {
  return (
    <div>
      {block.title && <h4 className="font-semibold text-sm mb-2">{block.title}</h4>}
      <div className="grid gap-3 grid-cols-2 sm:grid-cols-3">
        {block.data.items.map((item: MetricItem, i: number) => (
          <div key={i} className="flex flex-col gap-1 rounded-lg border border-border bg-card p-3">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{item.label}</span>
            <span className="text-xl font-bold text-foreground">{formatMetricValue(item.value, item.format)}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ============================================================================
// Inline Note
// ============================================================================

function InlineNote({ block }: { block: NoteBlock }) {
  const variant = block.variant === 'error' ? 'destructive' : 'default'
  return (
    <Alert variant={variant}>
      {block.data.title && <AlertTitle>{block.data.title}</AlertTitle>}
      <AlertDescription>{block.data.text}</AlertDescription>
    </Alert>
  )
}

// ============================================================================
// Inline Actions
// ============================================================================

function InlineActions({ block }: { block: ActionsBlock }) {
  return (
    <div>
      {block.title && <h4 className="font-semibold text-sm mb-2">{block.title}</h4>}
      <div className="flex flex-wrap gap-2">
        {block.data.items.map((item) => (
          <Button
            key={item.id}
            variant={item.variant === 'danger' ? 'destructive' : item.variant === 'secondary' ? 'secondary' : 'default'}
            size="sm"
          >
            {item.label}
          </Button>
        ))}
      </div>
    </div>
  )
}

// ============================================================================
// Inline Raw JSON
// ============================================================================

function InlineRawJson({ block }: { block: RawJsonBlock }) {
  const [isOpen, setIsOpen] = useState(!block.data.collapsed)
  return (
    <div>
      <button onClick={() => setIsOpen(!isOpen)} className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground">
        {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        {block.title ?? 'Raw Data'}
      </button>
      {isOpen && (
        <pre className="mt-2 bg-muted p-3 rounded-lg text-xs font-mono overflow-x-auto max-h-64">
          {JSON.stringify(block.data.json, null, 2)}
        </pre>
      )}
    </div>
  )
}

// ============================================================================
// Inline Graph (collapsible)
// ============================================================================

function InlineGraph({ block }: { block: GraphBlock }) {
  const [isExpanded, setIsExpanded] = useState(false)
  return (
    <div>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
      >
        {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        {block.title ?? 'Knowledge Graph'}
        <span className="text-xs text-muted-foreground font-normal">({block.data.paths.length} connections)</span>
      </button>
      {isExpanded && (
        <div className="mt-2 border rounded-lg p-3 bg-muted/30">
          <div className="space-y-1 text-sm">
            {block.data.paths.map((path, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="font-medium">{path.subject}</span>
                <span className="text-muted-foreground text-xs">—{path.predicate}→</span>
                <span className="font-medium">{path.object}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
